import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Box, Button, IconButton, Popover, Typography, Stepper, Step, StepLabel, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

import DefaultForm from './DefaultForm';
import { FieldArray, Formik } from 'formik';
import { InputComponents } from '../../../utilites/StepType';
import StepperCardComponent from './StepperCardComponent';
import CommonNodeSetting from '../otherComponent/CommonNodeSetting/CommonNodeSetting';
import { v4 as uuidv4 } from 'uuid';
import StepModify from '../otherComponent/StepModify';


interface IField {
  uid?: string;
  question: string;
  description: string;
  inputComponent: InputComponents;
  options: string[];
  children: IField[];
}

interface MainFormProps {
  fields: IField[];
  data: {
    title: string;
    label?: string;
  };
  actor: any;
  onComplete: any;
   onCompleteStatusUid: string;
}

type SignFormProps = {
  selectedNodeData: any;
  selectedNode: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
};
type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

interface NotificationPayload {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
}

// Helper to ensure all fields and children have a uid
const addUidsToFields = (fields: IField[]): IField[] =>
  (fields || []).map((field) => ({
    ...field,
    uid: field.uid || uuidv4(),
    children: Array.isArray(field.children)
      ? field.children.map((child) => ({
          ...child,
          uid: child.uid || uuidv4()
        }))
      : []
  }));

const ChecklistComponent = ({ selectedNodeData, onSubmit, onClose, selectedNode }: SignFormProps) => {
  const theme = useTheme();
  const [initialValue, setInitialValue] = useState<MainFormProps | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showFieldTypeSelector, setShowFieldTypeSelector] = useState(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: [],
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

  const openPopover = Boolean(anchorEl);

  // Memoized callbacks to prevent unnecessary re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
    console.log('📋 ChecklistComponent: Statuses loaded:', list.length);
    setStatuses(list);
  }, []);

  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
    console.log('🔔 ChecklistComponent: Notification changed:', payload);
    setNotificationPayload(payload);
  }, []);

  const handleEscalationChange = useCallback((payload: any) => {
    console.log('📞 ChecklistComponent: Escalation changed:', payload);
    setEscalationPayload(payload);
  }, []);

  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, handleSubmit: any) => {
    console.log('🔄 ChecklistComponent: Step type changing to:', newType);
    setFieldValue('stepType', newType);
    setFieldValue('componentType', newType);
    setFieldValue('data.title', newType);
    setTimeout(() => {
      handleSubmit();
    });
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>, showTypeSelector = false) => {
    setAnchorEl(event.currentTarget);
    setShowFieldTypeSelector(showTypeSelector);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

  useEffect(() => {
    if (selectedNodeData) {
      const { fields = [], data = {}, onComplete = {} } = selectedNodeData;

      //each field and children having uid
      const fieldsWithUids = addUidsToFields(fields);

      const title = selectedNode?.data?.label?.trim() || data?.label?.trim() || 'Untitled Checklist';
      const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];

      // Extract and map notification data from backend
      const backendNotification = selectedNode?.data?.step?.notification || {};
      console.log('🔍 ChecklistComponent: Backend notification data:', backendNotification);
      
      const mappedNotification = {
        scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
        onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
        notifyRoles: backendNotification.roles || [],
        notifyUsers: backendNotification.users || [],
        notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
          channel === 'IN-APP' ? 'In-App' : 
          channel === 'EMAIL' ? 'Email' :
          channel === 'SMS' ? 'SMS' : channel
        ) : []
      };

      console.log('🔍 ChecklistComponent: Mapped notification:', mappedNotification);

      setInitialValue({
        fields: fieldsWithUids,
        data: {
          title,
          label: title
        },
        actor: actor,
        onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
        onComplete: onComplete || {}
      });

      // Set notification payload separately to avoid type issues
      setNotificationPayload({
        conditions: [
          ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
          ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
        ],
        users: mappedNotification.notifyUsers || [],
        roles: mappedNotification.notifyRoles || [],
        channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
          ? mappedNotification.notificationChannel.map((channel: string) => 
              channel === 'In-App' ? 'IN-APP' : 
              channel === 'Email' ? 'EMAIL' :
              channel === 'SMS' ? 'SMS' : channel.toUpperCase()
            )
          : []
      });

      // Initialize escalation payload from existing node data
      setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.

      console.log('🔍 ChecklistComponent: Set notification payload with users:', mappedNotification.notifyUsers, 'roles:', mappedNotification.notifyRoles);
    }
  }, [selectedNodeData, selectedNode]);

  const PopoverModal = ({ push }: any) => (
    <Popover open={openPopover} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <DefaultForm
        onAddField={(data) => {
          const { type, label } = data;
          const fieldData: IField = {
            uid: uuidv4(),
            question: label,
            description: '',
            inputComponent: InputComponents[type as keyof typeof InputComponents],
            options: [],
            children: []
          };
          push(fieldData);
          handleClose();
        }}
        showFieldTypeSelector={showFieldTypeSelector}
      />
    </Popover>
  );

  return (
    initialValue && (
      <Formik
        initialValues={initialValue}
        enableReinitialize
        onSubmit={(values) => {
          // Ensure all fields and children have uid before submit
          const selectedStatus = statuses.find((s) => s.uid === values.onCompleteStatusUid);
          
          const updatedData = {
            ...values,
            fields: addUidsToFields(values.fields),
            onComplete: selectedStatus ? { status: selectedStatus } : {}
          };

          // Add notification and escalation to the payload (following VideoCallForm pattern)
          const finalData = {
            ...updatedData,
            notification: notificationPayload,
            escalation: escalationPayload
          };

          console.log('🚀 ChecklistComponent: Submitting with notification payload:', notificationPayload);
          onSubmit(finalData);
          onClose();
        }}
      >
        {({ values, setFieldValue, handleBlur, handleSubmit }) => (
          <Box component="form" onSubmit={handleSubmit}>
            <FieldArray name="fields">
              {({ push, remove }) => (
                <>
            <Box display="flex" justifyContent="space-between" alignItems="center" padding={2} bgcolor={theme.palette.background.paper}>
              {/* Left Side — Title or Editable Input */}
              <Box display="flex" alignItems="center" gap={1}>
                {isEditingTitle ? (
                  <>
                    <TextField
                      name="data.title"
                      value={values.data.title}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFieldValue('data.title', newValue);
                        setFieldValue('data.label', newValue);
                      }}
                      onBlur={handleBlur}
                      size="small"
                      autoFocus
                    />
                    <IconButton onClick={() => setIsEditingTitle(false)}>
                      <CheckIcon />
                    </IconButton>
                  </>
                ) : (
                  <Typography variant="h6" fontWeight={500}>
                    {values.data.title}
                  </Typography>
                )}
              </Box>

              {/* Right Side — Edit + StepModify + Add */}
              <Box display="flex" alignItems="center" gap={1}>
                {!isEditingTitle && (
                  <IconButton onClick={() => setIsEditingTitle(true)}>
                    <EditIcon />
                  </IconButton>
                )}

                {selectedNode && (
                  <StepModify
                    selectedNode={selectedNode}
                    onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, handleSubmit)}
                  />
                )}

                <IconButton onClick={(e) => handleClick(e, false)} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>



                  <PopoverModal push={push} />

                  {values.fields.length > 0 && (
                    <Box display="flex" flexDirection="row" justifyContent="center" px={3} pt={3} gap={4}>
                      {/* Stepper */}
                      <Box minWidth="20px">
                        <Stepper activeStep={activeStep} orientation="vertical">
                          {values.fields.map((field, index) => (
                            <Step key={field.uid || `step-${index}`} onClick={() => setActiveStep(index)} completed={false}>
                              <StepLabel sx={{ cursor: 'pointer' }} />
                            </Step>
                          ))}
                        </Stepper>
                      </Box>

                      {/* Step Content */}
                      <StepperCardComponent
                        {...{
                          activeStep,
                          values,
                          onSubmit,
                          setFieldValue,
                          handleChange: () => {},
                          handleBlur
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </FieldArray>
            
            <Box mx={3} mt={2}>
              <CommonNodeSetting
                selectedNode={selectedNode}
                onStatusesLoaded={handleStatusesLoaded}
                onNotificationChange={handleNotificationChange}
                notificationInitialValues={notificationInitialValues}
                escalationPayload={escalationPayload}
                onEscalationChange={handleEscalationChange}
              />
            </Box>

            {/* Footer Buttons */}
            <Box
              sx={{
                padding: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 4
              }}
            >
              <Button type="button" color="error" onClick={onClose} sx={{'&:hover': { backgroundColor: theme.palette.error.light }}}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary" sx={{'&:hover': { backgroundColor: theme.palette.primary.dark }}}>
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    )
  );
};

export default memo(ChecklistComponent);
